import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { BreakpointTrackerContext } from '@jutro/layout';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { WMICEditForm, WMICAddressDetails } from 'wmic-pe-components-platform-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useWizardModals } from 'wmic-pe-portals-wizard-components-ui';
import {
    CONSTANTS,
    WMICAddressDetailsUtil,
    WMICRichTextUtil,
    DeviceBreakpoint,
    Position,
    PAConstants,
    JURISDICTIONS,
    RIDE_SHARING_COMPANIES,
    JobType,
    WMICLogger,
    MODAL_CONSTANTS,
} from 'wmic-pe-portals-utils-js';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { AddressService_Ext } from 'wmic-pe-capability-address';
import { GatewayServiceUtil_Ext } from 'wmic-pe-capability-gateway-common-react';
import { RISK_PATHS } from 'wmic-pe-portals-utils-js/StringConstants';
import WMICVehiclesUsageCommercial from './WMICVehiclesUsageCommercial/WMICVehiclesUsageCommercial';
import metadata from './WMICVehiclesUsageComponent.metadata.json5';
import messages from './WMICVehiclesUsageComponent.messages.js';
import styles from './WMICVehicleUsageComponent.module.scss';

let _revertedBusinessSegment = false;

function WMICVehiclesUsageComponent({
    id,
    vehicleVM,
    onValidate,
    isEditMode,
    showErrors,
    jobVM,
    reselectVehicle,
    revalidateVIN,
    onBusinessSegmentChanged,
    updateVehicle,
    updateWizardData,
}) {
    const viewModelService = useContext(ViewModelServiceContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    const translator = useContext(TranslatorContext);
    const { authHeader } = useAuthentication();
    const { setWizardLoading, showError } = useWizardModals();

    const createAddressVM = model =>
        viewModelService.create(model, 'pc', 'wmic.edge.ca.capabilities.address.dto.AddressDTO');

    const [newPrimaryLocation, setNewPrimaryLocation] = useState(createAddressVM({ country: CONSTANTS.COUNTRY.CA }));
    const [newRatedGarageLocation, setNewRatedGarageLocation] = useState(
        createAddressVM({ country: CONSTANTS.COUNTRY.CA })
    );
    const [allLocationAddresses, setAllLocationAddresses] = useState(
        _.get(jobVM, 'baseData.accountAddresses_WMIC.value', [])
    );
    const [selectedPrimaryAddress, setSelectedPrimaryAddress] = useState(
        _.get(vehicleVM, 'primaryLocation_WMIC.value') || _.get(jobVM, 'baseData.accountAddresses_WMIC.value[0]', {})
    );
    const [selectedGarageAddress, setSelectedGarageAddress] = useState(
        _.get(vehicleVM, 'garageLocation_WMIC.value') || _.get(jobVM, 'baseData.accountAddresses_WMIC.value[0]', {})
    );
    const [isNewLocationFormVisible, setNewLocationFormVisible] = useState(false);
    const [isNewRatedGarageFormVisible, setNewRatedGarageFormVisible] = useState(false);
    const [businessSegmentErrorMsg, setBusinessSegmentErrorMsg] = useState();
    const [availablePrimaryUses, setAvailablePrimaryUses] = useState(
        _.get(vehicleVM, 'primaryUse.aspects.availableValues', [])
    );
    const [availableRideSharingCompanies, setAvailableRideSharingCompanies] = useState(
        _.get(vehicleVM, 'rideSharingCompany_WMIC.aspects.availableValues', [])
    );
    const [oldBusinessSegment, setOldBusinessSegment] = useState(_.get(vehicleVM, 'businessSegment_WMIC'));

    const isPrimaryUseDisabled = _.get(vehicleVM, 'vehicleType.value.code') === PAConstants.motorhomeVehicleType;

    const componentValidMap = useMemo(() => ({}), []);

    const componentValidation = useCallback(
        (valid, componentId) => {
            componentValidMap[componentId] = valid;
            if (
                !(_.get(vehicleVM, 'businessSegment_WMIC.value.code') === PAConstants.vehicleBusinessSegmentCommercial)
            ) {
                onValidate(componentValidMap.vehiclesUsageComponent, id);
            } else {
                const isCommercialVehicleContainerVisible = _.get(
                    vehicleVM,
                    'commercialVehicle_WMIC.commercialVehicleContainer.aspects.ocular',
                    false
                );
                onValidate(
                    componentValidMap.vehiclesUsageComponent &&
                        (isCommercialVehicleContainerVisible ? componentValidMap.vehiclesUsageCommercial : true),
                    id
                );
            }
        },
        [onValidate, componentValidMap]
    );

    useEffect(() => {
        if (onValidate) {
            componentValidation(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidate]);

    const setCommercialVM = newCommercialVM => {
        _.set(vehicleVM, 'commercialVehicle_WMIC', newCommercialVM.value);
        updateVehicle(vehicleVM);
    };

    const mapTypelistNames = type => ({
        ...type,
        name: {
            id: type.name,
            defaultMessage: type.name,
        },
    });

    const isGarageLocationOntario = () =>
        _.get(vehicleVM, 'garageLocation_WMIC.state.value.code') === JURISDICTIONS.ONTARIO;

    const isGarageLocationMaritime = () =>
        JURISDICTIONS.MARITIMES.includes(_.get(vehicleVM, 'garageLocation_WMIC.state.value.code'));

    const isGarageLocationPE = () =>
        _.get(vehicleVM, 'garageLocation_WMIC.state.value.code') === JURISDICTIONS.PRINCE_EDWARD_ISLAND;

    const isGarageLocationQC = () => _.get(vehicleVM, 'garageLocation_WMIC.state.value.code') === JURISDICTIONS.QUEBEC;

    const isCommercialVehicle = () =>
        _.get(vehicleVM, 'businessSegment_WMIC.value.code') === PAConstants.vehicleBusinessSegmentCommercial;

    const isVehicleType = typeCode => _.get(vehicleVM, 'vehicleType.value.code') === typeCode;

    const isVehicleTypeAuto = () => isVehicleType(PAConstants.personalAutoVehicleType);

    const isVehicleTypeMotorCycle = () => isVehicleType(PAConstants.motorcycleVehicleType);

    const isVehicleTypeMotorhome = () => isVehicleType(PAConstants.motorhomeVehicleType);

    const isVehicleTypeMoped = () => isVehicleType(PAConstants.mopedVehicleType);

    const isVehicleTypeTrailer = () => isVehicleType(PAConstants.trailerVehicleType);

    const isVehicleTypeAutoMotorCyleOrMotorHome = () =>
        isVehicleTypeAuto() || isVehicleTypeMotorCycle() || isVehicleTypeMotorhome();

    const selectDefaultPrimaryUse = () => {
        let defaultPrimaryUse = null;

        // if there is no value then default it
        if (!_.get(vehicleVM, 'primaryUse.value')) {
            if (_.get(vehicleVM, 'vehicleType.value')) {
                // eslint-disable-next-line default-case
                switch (_.get(vehicleVM, 'vehicleType.value.code')) {
                    case PAConstants.motorcycleVehicleType:
                        defaultPrimaryUse = CONSTANTS.VEHICLE_PRIMARY_USE.COMMUTING;
                        break;
                    case PAConstants.motorhomeVehicleType:
                    case PAConstants.trailerVehicleType:
                    case PAConstants.atvVehicleType:
                    case PAConstants.snowVehicleType:
                        defaultPrimaryUse = CONSTANTS.VEHICLE_PRIMARY_USE.PLEASURE;
                        break;
                }
            }
        }

        if (defaultPrimaryUse) {
            const tempPrimaryUse = _.find(
                _.get(vehicleVM, 'primaryUse.aspects.availableValues'),
                primaryUse => primaryUse.code.toLowerCase() === defaultPrimaryUse
            );
            _.set(vehicleVM, 'primaryUse.value', tempPrimaryUse);
            updateVehicle(vehicleVM);
        }
    };

    // TODO BPN-5825 - move to BE
    const populateAvailablePrimaryUse = () => {
        let primaryUseCodes = [];
        // TODO BPN-5825 - move to BE (used in Vehicle Info component)
        // setValidTrailerSubTypes();

        if (isVehicleTypeMotorCycle() || isVehicleTypeMoped()) {
            primaryUseCodes = [
                CONSTANTS.VEHICLE_PRIMARY_USE.COMMUTING,
                CONSTANTS.VEHICLE_PRIMARY_USE.PLEASURE,
                CONSTANTS.VEHICLE_PRIMARY_USE.TOURING,
            ];
        } else if (isGarageLocationQC()) {
            if (_.get(vehicleVM, 'businessSegment_WMIC.value.code') === PAConstants.vehicleBusinessSegmentCommercial) {
                primaryUseCodes = [CONSTANTS.VEHICLE_PRIMARY_USE.BUSINESS];
            } else if (_.get(vehicleVM, 'vehicleType.value.code') === PAConstants.personalAutoVehicleType) {
                primaryUseCodes = [
                    CONSTANTS.VEHICLE_PRIMARY_USE.BUSINESS,
                    CONSTANTS.VEHICLE_PRIMARY_USE.COMMUTING,
                    CONSTANTS.VEHICLE_PRIMARY_USE.PLEASURE,
                    CONSTANTS.VEHICLE_PRIMARY_USE.PARK_RIDE,
                ];
            } else {
                primaryUseCodes = [
                    CONSTANTS.VEHICLE_PRIMARY_USE.BUSINESS,
                    CONSTANTS.VEHICLE_PRIMARY_USE.COMMUTING,
                    CONSTANTS.VEHICLE_PRIMARY_USE.PLEASURE,
                ];
            }
        } else if (isCommercialVehicle()) {
            primaryUseCodes = [CONSTANTS.VEHICLE_PRIMARY_USE.BUSINESS, CONSTANTS.VEHICLE_PRIMARY_USE.FARM];
        } else {
            primaryUseCodes = [
                CONSTANTS.VEHICLE_PRIMARY_USE.BUSINESS,
                CONSTANTS.VEHICLE_PRIMARY_USE.CLERGYMAN,
                CONSTANTS.VEHICLE_PRIMARY_USE.COMMUTING,
                CONSTANTS.VEHICLE_PRIMARY_USE.FARM,
                CONSTANTS.VEHICLE_PRIMARY_USE.PLEASURE,
            ];
        }

        const rawPrimaryUses = _.filter(_.get(vehicleVM, 'primaryUse.aspects.availableValues'), primaryUse =>
            _.includes(primaryUseCodes, primaryUse.code)
        );

        setAvailablePrimaryUses(_.map(rawPrimaryUses, mapTypelistNames));

        // if the current value isn't in the new set then set it to null
        if (
            _.get(vehicleVM, 'primaryUse.value') &&
            !_.includes(primaryUseCodes, _.get(vehicleVM, 'primaryUse.value.code'))
        ) {
            _.set(vehicleVM, 'primaryUse.value', null);
            updateVehicle(vehicleVM);
        } else if (!_.get(vehicleVM, 'primaryUse.value')) {
            selectDefaultPrimaryUse();
        }
    };

    const setLocations = () => {
        if (_.get(vehicleVM, 'primaryLocation_WMIC.value') === undefined && selectedPrimaryAddress) {
            _.set(vehicleVM, 'primaryLocation_WMIC', selectedPrimaryAddress);
        }

        if (_.get(vehicleVM, 'garageLocation_WMIC.value') === undefined && selectedGarageAddress) {
            _.set(vehicleVM, 'garageLocation_WMIC', selectedGarageAddress);
        }
        updateVehicle(vehicleVM);
    };

    const isBusinessSegmentReadOnly = () =>
        [PAConstants.motorhomeVehicleType, PAConstants.motorcycleVehicleType].includes(
            _.get(vehicleVM, 'vehicleType.value.code')
        );

    const handleUsedOutsideProvinceChange = (value, path) => {
        if (isEditMode) {
            _.set(vehicleVM, path, value);
            _.set(vehicleVM, 'outsideProvinceDays_WMIC.value', null);
            _.set(vehicleVM, 'outsideProvinceReason_WMIC.value', null);
            updateVehicle(vehicleVM);
        }
    };

    const handleUsedOutsideCanadaChange = (value, path) => {
        if (isEditMode) {
            _.set(vehicleVM, path, value);
            _.set(vehicleVM, 'outOfCountryMonths_WMIC.value', null);
            _.set(vehicleVM, 'outOfCountryDesc_WMIC.value', null);
            updateVehicle(vehicleVM);
        }
    };

    const handleBusinessSegmentChange = (value, path) => {
        if (isEditMode) {
            _.set(vehicleVM, path, value);

            if (_revertedBusinessSegment) {
                _revertedBusinessSegment = false;
            } else {
                revalidateVIN().then(vehicleSearchErrors => {
                    setBusinessSegmentErrorMsg(vehicleSearchErrors);

                    if (vehicleSearchErrors && vehicleSearchErrors?.length) {
                        _.set(vehicleVM, `${path}.value`, oldBusinessSegment);
                        _revertedBusinessSegment = true;
                    }
                });
            }

            _.set(vehicleVM, 'rateReason_WMIC', undefined);
            _.set(vehicleVM, 'businessSegmentHasChanged', true);

            if(isVehicleTypeTrailer) {
                _.set(vehicleVM, 'trailerSubType_WMIC', undefined);
            }

            onBusinessSegmentChanged();
            updateVehicle(vehicleVM);
        }
    };

    const getReselectVehicleMessage = vehicleSearchErrors =>
        !vehicleSearchErrors?.length
            ? WMICRichTextUtil.translateRichText(translator(messages.reselectVehicle))
            : translator(messages.businessSegmentCannotBeUpdated);

    const handleClickAddNewPrimaryLocation = () => {
        setNewLocationFormVisible(true);
    };

    const handleClickAddRatedGarageLocation = () => {
        setNewRatedGarageFormVisible(true);
    };

    const handleCancelNewPrimaryLocation = () => {
        setNewPrimaryLocation(createAddressVM({ country: CONSTANTS.COUNTRY.CA }));
        setNewLocationFormVisible(false);
    };

    const handleCancelNewRatedGarageLocation = () => {
        setNewRatedGarageFormVisible(false);
    };

    const handleChangePrimaryVehicleLocation = value => {
        const newSelectedAddress = allLocationAddresses.find(address => address?.publicID === value);

        setSelectedPrimaryAddress(newSelectedAddress);

        vehicleVM.primaryLocation_WMIC = newSelectedAddress;
        if (vehicleVM?.rateAtPrimaryLocation_WMIC?.value) {
            vehicleVM.ratingJurisdiction = newSelectedAddress?.state;
        }
        updateVehicle(vehicleVM);
    };

    const handleChangeRatedGarageLocation = value => {
        const newSelectedAddress = allLocationAddresses.find(address => address?.publicID === value);

        setSelectedGarageAddress(newSelectedAddress);

        vehicleVM.garageLocation_WMIC = newSelectedAddress;
        vehicleVM.ratingJurisdiction = newSelectedAddress?.state;
        updateVehicle(vehicleVM);
    };

    const handleSaveNewPrimaryLocation = () => {
        const accountAddresses = jobVM?.baseData?.accountAddresses_WMIC;
        const oldLocationPublicID = selectedPrimaryAddress?.publicID;

        setWizardLoading(true);
        AddressService_Ext.saveNewLocation(jobVM?.baseData?.accountNumber?.value, newPrimaryLocation?.value, authHeader)
            .then(result => {
                accountAddresses.pushElement(result);

                setAllLocationAddresses(accountAddresses?.value);

                setSelectedPrimaryAddress(result);
                setNewLocationFormVisible(false);
                setNewPrimaryLocation(createAddressVM({ country: CONSTANTS.COUNTRY.CA }));

                jobVM.baseData.accountAddresses_WMIC = accountAddresses?.value;
                updateWizardData(jobVM);

                vehicleVM.primaryLocation_WMIC = result;

                if (vehicleVM?.rateAtPrimaryLocation_WMIC?.value) {
                    vehicleVM.ratingJurisdiction = result?.state;
                }
                updateVehicle(vehicleVM);

                return GatewayServiceUtil_Ext.postOnChange(
                    jobVM?.baseData?.jobType?.value?.code,
                    jobVM.value,
                    authHeader
                );
            })
            .then(result => {
                jobVM.value = result;
                updateWizardData(jobVM);
            })
            .catch(error => {
                WMICLogger.error('Save new location failed', error);
                handleChangePrimaryVehicleLocation(oldLocationPublicID);
                showError({
                    title: translator(messages.errorTitle),
                    message: translator(messages.saveLocationErrorMessage),
                    status: MODAL_CONSTANTS.STATUS.WARNING,
                    icon: MODAL_CONSTANTS.ICON.ERROR,
                });
            })
            .finally(() => {
                setWizardLoading(false);
            });
    };

    const handleSaveNewRatedGarageLocation = () => {
        const accountAddresses = jobVM?.baseData?.accountAddresses_WMIC;
        const oldLocationPublicID = selectedGarageAddress?.publicID;

        setWizardLoading(true);
        AddressService_Ext.saveNewLocation(
            jobVM?.baseData?.accountNumber?.value,
            newRatedGarageLocation?.value,
            authHeader
        )
            .then(result => {
                accountAddresses.pushElement(result);

                setAllLocationAddresses(accountAddresses?.value);

                setSelectedGarageAddress(result);
                setNewRatedGarageFormVisible(false);
                setNewRatedGarageLocation(createAddressVM({ country: CONSTANTS.COUNTRY.CA }));

                jobVM.baseData.accountAddresses_WMIC = accountAddresses?.value;
                updateWizardData(jobVM);

                vehicleVM.garageLocation_WMIC = result;
                vehicleVM.ratingJurisdiction = result?.state;
                updateVehicle(vehicleVM);

                return GatewayServiceUtil_Ext.postOnChange(
                    jobVM?.baseData?.jobType?.value?.code,
                    jobVM.value,
                    authHeader
                );
            })
            .then(result => {
                jobVM.value = result;
                updateWizardData(jobVM);
            })
            .catch(error => {
                WMICLogger.error('Save new location failed', error);
                handleChangeRatedGarageLocation(oldLocationPublicID);
                showError({
                    title: translator(messages.errorTitle),
                    message: translator(messages.saveLocationErrorMessage),
                    status: MODAL_CONSTANTS.STATUS.WARNING,
                    icon: MODAL_CONSTANTS.ICON.ERROR,
                });
            })
            .finally(() => {
                setWizardLoading(false);
            });
    };

    const handleChangeRateAtPrimaryLocation = value => {
        const garageLocationState = _.get(vehicleVM, 'garageLocation_WMIC.value.state');
        const primaryLocationState = _.get(vehicleVM, 'primaryLocation_WMIC.value.state');

        _.set(vehicleVM, 'rateAtPrimaryLocation_WMIC', value);
        if (value) {
            _.set(vehicleVM, 'ratingJurisdiction', primaryLocationState);
        } else if (garageLocationState) {
            _.set(vehicleVM, 'ratingJurisdiction', garageLocationState);
        }
        updateVehicle(vehicleVM);
    };

    const getShareCompanies = () => {
        let rideSharingCompanyValues = _.get(vehicleVM, 'rideSharingCompany_WMIC.aspects.availableValues');
        if (!isGarageLocationOntario()) {
            rideSharingCompanyValues = _.filter(
                rideSharingCompanyValues,
                company => company.code !== RIDE_SHARING_COMPANIES.URIDE
            );
        }

        if (!isGarageLocationMaritime() && !isGarageLocationOntario()) {
            rideSharingCompanyValues = _.filter(
                rideSharingCompanyValues,
                company => company.code !== RIDE_SHARING_COMPANIES.LYFT
            );
        }

        if (!isGarageLocationPE()) {
            rideSharingCompanyValues = _.filter(
                rideSharingCompanyValues,
                company => company.code !== RIDE_SHARING_COMPANIES.REDRIDE_KARI
            );
        }

        setAvailableRideSharingCompanies(_.map(rideSharingCompanyValues, mapTypelistNames));
    };

    const vehiclePrimaryAddress = _.get(vehicleVM, 'primaryLocation_WMIC.value');
    const garageLocationAddress = _.get(vehicleVM, 'garageLocation_WMIC.value');

    useEffect(() => {
        populateAvailablePrimaryUse();
    }, [_.get(vehicleVM, 'publicID.value'), _.get(vehicleVM, 'businessSegment_WMIC.value.code')]);

    useEffect(() => {
        setLocations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleVM.publicID.value]);

    useEffect(() => {
        if (!_.get(vehicleVM, 'usedForRideSharing_WMIC.value')) {
            _.set(vehicleVM, 'rideSharingCompany_WMIC.value', null);
            _.set(vehicleVM, 'rideSharingOtherDesc_WMIC.value', null);
            updateVehicle(vehicleVM);
            setAvailableRideSharingCompanies([]);
        } else {
            getShareCompanies();
        }
    }, [_.get(vehicleVM, 'primaryLocation_WMIC.value.state'), _.get(vehicleVM, 'usedForRideSharing_WMIC.value')]);

    const overrideProps = {
        '@field': {
            parentNode: vehicleVM,
            readOnly: !isEditMode,
        },
        businessSegment: {
            readOnly: !isEditMode || isBusinessSegmentReadOnly(),
        },
        commercialVehicleComponentContainer: {
            visible: _.get(vehicleVM, 'commercialVehicle_WMIC.commercialVehicleContainer.aspects.ocular', false),
        },
        primaryUse: {
            availableValues: availablePrimaryUses,
            readOnly: !isEditMode || isPrimaryUseDisabled,
        },
        rideSharingCompany: {
            availableValues: availableRideSharingCompanies,
        },
        mileageContainer: {
            visible: _.get(vehicleVM, 'mileageContainer.aspects.ocular', false),
        },
        odometerReading: {
            visible: isVehicleTypeAutoMotorCyleOrMotorHome(),
        },
        dateReadingTaken: {
            visible: isVehicleTypeAutoMotorCyleOrMotorHome(),
        },
        rideSharingContainer: {
            visible: _.get(vehicleVM, 'rideSharingContainer.aspects.ocular', false),
        },
        motorHomeRiskContainer: {
            visible: _.get(vehicleVM, 'motorhomeRisksContainer.aspects.ocular', false),
        },
        addLocationBtnContainer: {
            visible: isEditMode,
        },
        addLocationBtn: {
            disabled: isNewLocationFormVisible,
        },
        primaryAddressDetailsContainer: {
            visible: isNewLocationFormVisible && isEditMode,
        },
        primaryVehicleLocationDropdown: {
            availableValues: allLocationAddresses.map(elem => ({
                code: elem.publicID,
                description: elem.displayName,
                ...elem,
            })),
            value: {
                code: vehiclePrimaryAddress?.publicID,
                description: vehiclePrimaryAddress?.displayName,
                ...vehiclePrimaryAddress,
            },
            path: 'primaryLocation_WMIC',
            onValueChange: handleChangePrimaryVehicleLocation,
            labelPosition: breakpoint === DeviceBreakpoint.DESKTOP ? Position.LEFT : Position.TOP,
            showRequired: true,
        },
        primaryAddressDetailsComponent: {
            onValidate: setComponentValidation,
            addressVM: newPrimaryLocation,
            onCancel: handleCancelNewPrimaryLocation,
            onSave: handleSaveNewPrimaryLocation,
            showErrors,
            readOnlyCountry: true,
            addressType: CONSTANTS.ADDRESS_TYPE.POLICY_LOCATION,
            jobNumber:
                jobVM?.baseData?.jobType?.value?.code === JobType.SUBMISSION
                    ? jobVM?.quoteID?.value
                    : jobVM?.jobID?.value,
            showUseAccountAddressButton: true,
            accountAddress: jobVM?.baseData?.accountHolder?.primaryAddress?.value
        },
        addRatedGarageLocationBtnContainer: {
            visible: isEditMode && !_.get(vehicleVM, 'rateAtPrimaryLocation_WMIC.value', true),
        },
        addRatedGarageLocationBtn: {
            disabled: isNewRatedGarageFormVisible,
        },
        ratedGarageAddressDetailsContainer: {
            visible: isNewRatedGarageFormVisible && isEditMode,
        },
        ratedGarageLocationDropdown: {
            availableValues: allLocationAddresses.map(elem => ({
                code: elem.publicID,
                description: elem.displayName,
                ...elem,
            })),
            value: {
                code: garageLocationAddress?.publicID,
                description: garageLocationAddress?.displayName,
                ...garageLocationAddress,
            },
            path: 'garageLocation_WMIC',
            onValueChange: handleChangeRatedGarageLocation,
            visible: !_.get(vehicleVM, 'rateAtPrimaryLocation_WMIC.value', true),
            labelPosition: breakpoint === DeviceBreakpoint.DESKTOP ? Position.LEFT : Position.TOP,
            showRequired: true,
        },
        ratedGarageAddressDetailsComponent: {
            onValidate: setComponentValidation,
            addressVM: newRatedGarageLocation,
            onCancel: handleCancelNewRatedGarageLocation,
            onSave: handleSaveNewRatedGarageLocation,
            showErrors,
            readOnlyCountry: true,
        },
        vehiclesUsageCommercial: {
            vehicleVM,
            commercialVM: _.get(vehicleVM, 'commercialVehicle_WMIC'),
            setCommercialVM,
            showErrors,
            onValidate: componentValidation,
            isEditMode,
            rateAsOfDate: _.get(jobVM, 'baseData.rateAsOfDate.value'),
            vehicles: _.get(jobVM, RISK_PATHS.VEHICLES_PATH),
            policyNumber: _.get(jobVM, 'baseData.policyAddress.value.state'),
            inceptionDate: _.get(jobVM, 'baseData.inceptionDate_WMIC.value'),
            periodStartDate: _.get(jobVM, 'baseData.periodStartDate.value'),
        },
        reselectVehicleContainer: {
            visible: reselectVehicle,
        },
        reselectVehicleMsg: {
            content: getReselectVehicleMessage(businessSegmentErrorMsg),
        },
        usedForCarSharingContainer: {
            visible: _.get(vehicleVM, 'showUsedForCarSharingContainer.aspects.ocular', false),
        },
    };

    const resolvers = {
        resolveComponentMap: {
            WMICEditForm,
            WMICAddressDetails,
            WMICVehiclesUsageCommercial,
        },
        resolveCallbackMap: {
            onClickAddNewPrimaryLocation: handleClickAddNewPrimaryLocation,
            onClickAddRatedGarageLocation: handleClickAddRatedGarageLocation,
            onRateAtPrimaryLocation: handleChangeRateAtPrimaryLocation,
            onBusinessSegmentChange: handleBusinessSegmentChange,
            onUsedOutsideProvinceChange: handleUsedOutsideProvinceChange,
            onUsedOutsideCanadaChange: handleUsedOutsideCanadaChange,
        },
        resolveClassNameMap: styles,
    };

    return (
        <ViewModelForm
            model={vehicleVM}
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            onModelChange={updateVehicle}
            onValidationChange={setComponentValidation}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            showErrors={showErrors}
        />
    );
}

export default WMICVehiclesUsageComponent;
